import { 
  Box, 
  Autocomplete, 
  TextField, 
  Grid, 
  Alert, 
  Button, 
  IconButton, 
  Menu, 
  MenuItem, 
  Checkbox, 
  FormControlLabel
} from '@mui/material';
import { 
  ArrowBackIos, 
  Search, 
  KeyboardArrowDown
} from '@mui/icons-material';
import { useEffect, useState, useContext } from 'react';
import DeactivateIcon from '../utils/DeactivateIcon';
import StoreContext from '../context/StoreContext'
import UserContext from '../context/UserContext';
import PropTypes from 'prop-types';
import { formatSettingsParam } from '../utils/SettingsFormatter';
import WarningModal from './WarningModal';
import FeedbackContext from '../context/FeedbackContext';

const SettingsStoreList = ({onClose, settingLabel, settingSelected, fullStoreList, openSuccessModal, closeSettings}) => {

  const { username } = useContext(UserContext);
  const { setIsLoading } = useContext(FeedbackContext);

  const {
    updateSetting
  } = useContext(StoreContext);

  const [anchorEl, setAnchorEl] = useState(null);
  const [storeList, setStoreList] = useState([]);
  const [deactivatedStoreList, setDeactivatedStoreList] = useState([])
  const [inputValue, setInputValue] = useState([]);
  const [showModal, setShowModal] = useState(false)

  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    const selectedStores = fullStoreList.filter(store => store[settingSelected] === '1');
    const mappedStores = selectedStores.map(store => ({...store, checked: false}));
    setStoreList(mappedStores)
  },[fullStoreList, settingSelected])

  const addStore = () => {
    let storesChecked = [...inputValue].map(store => ({...store, checked: false}));
    let mappedStores = [...storeList.concat(storesChecked)];
    const uniqueStores = mappedStores.filter((store, index) => index === mappedStores.findIndex(storeMapped => storeMapped.value === store.value));
    const sortStores = uniqueStores.sort((a,b) => a.value - b.value);
    setStoreList(sortStores);
    setDeactivatedStoreList(prevStores => prevStores.filter((store) => !uniqueStores.some(addedStore => store.value === addedStore.value)))
    setInputValue([])
  }

  const handleStoreCheckBox = () => {
    let checkAllStores = [];
    if(storeList.filter(store => store.checked).length !== storeList.length) {
      checkAllStores = [...storeList].map(store => ({...store, checked: true}));
    } else {
      checkAllStores = [...storeList].map(store => ({...store, checked: false}));
    }
    setStoreList(checkAllStores)
  };

  const handleCheckStore = (storeIndex) => {
    let storeToUpdate = [...storeList];
    storeToUpdate[storeIndex].checked = !storeToUpdate[storeIndex].checked;
    setStoreList(storeToUpdate);
  }

  const handleSearchValue = (event, newValue) => {
    setInputValue(newValue)
  }

  const deactivateSelected = () => {
    if(storeList.length > 0) {
      const deactivatedStoresFiltered = storeList.filter(store => store.checked);
      const storesFiltered = storeList.filter(store => !store.checked);
      setDeactivatedStoreList(deactivatedStoresFiltered)
      setStoreList(storesFiltered)
    }
    closeModal()
  }

  const saveChanges = async () => {
    const param = formatSettingsParam(settingSelected);

    const activatedStoreList = [...storeList].map(({checked, ...rest}) => {
      return rest;
    })

    setIsLoading(true)
    await updateSetting(settingSelected, param, username, 'list', activatedStoreList, deactivatedStoreList)
    setIsLoading(false)
    openSuccessModal()
    closeSettings()
  }

  const closeModal = () => {
    setShowModal(false);
  }

  const openModal = () => {
    if(storeList.filter(store => store.checked).length > 0) {
      setShowModal(true);
    }
  }

  function checkStoreStatus(operation) {
    if(operation === 'checked')
      return storeList.length > 0 && storeList.filter(store => store.checked).length === storeList.length
    return storeList.length > 0 && storeList.filter(store => store.checked).length > 0 && storeList.filter(store => store.checked).length !== storeList.length
  }

  return (
    <Box sx={{marginTop: '30px'}}>
      <div className='mb-16 cursor-pointer' onClick={onClose}>
        <ArrowBackIos fontSize='small'/>
        <span>Back</span>
      </div>
      <div className='title-container'>
        {settingLabel} settings
      </div>
      <Box sx={{ border: '1px solid', borderColor: 'grey.300', borderRadius: 1 }}>
        {
          storeList.length === 0 && (
            <Alert variant="filled" severity="error" sx={{margin: '20px 20px 0 20px'}}>
              {settingLabel} has not been activated in any stores.
            </Alert>
          )
        }
        <Grid container spacing={2} sx={{padding: '25px', display: 'flex', alignItems: 'center'}}>
          <Grid item xs={10}>
            <Autocomplete
              sx={{
                overflow: 'auto',
                maxHeight: '8em',
                paddingY: '0.3em'
              }}
              forcePopupIcon	
              limitTags={3}
              disableCloseOnSelect
              disableListWrap
              fullWidth
              size="small"
              multiple
              options={fullStoreList}
              getOptionLabel={(option) => option.label}
              handleHomeEndKeys
              value={inputValue}
              onChange={handleSearchValue}
              renderInput={(params) => (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Search sx={{marginRight: '0.25em'}}/>
                  <TextField
                    {...params}
                    label="Search Stores..."
                  />
                </Box>
              )}
            />
          </Grid>
          <Grid item xs={2} justifyContent='flex-start'>
            <Button className="button white small" onClick={() => addStore()}>Add</Button>
          </Grid>
          <Grid item container xd={12} justifyContent='space-between'>
            <Grid item xs={3}>
              <Box sx={{display: 'flex', justifyContent:'space-between', alignItems:'center', border: '1px solid', borderColor: 'grey.300', borderRadius: 1 }}>
                <Box sx={{marginLeft: 2}}>
                  <FormControlLabel
                    label="Actions"
                    control={
                      <Checkbox
                        checked={checkStoreStatus('checked')}
                        indeterminate={checkStoreStatus('indeterminate')}
                        onChange={handleStoreCheckBox}
                      />
                    }
                  />
                </Box>
                <IconButton
                  onClick={handleClick}
                  size="small"
                  sx={{ ml: 2 }}
                  aria-controls={open ? 'settings-menu' : undefined}
                  aria-haspopup="true"
                  aria-expanded={open ? 'true' : undefined}
                >
                  <KeyboardArrowDown/>
                </IconButton>
              </Box>
              <Menu
                anchorEl={anchorEl}
                id="settings-menu"
                open={open}
                onClose={handleClose}
                onClick={handleClose}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              >
                <MenuItem onClick={() => {handleClose(); openModal()}}>
                  <DeactivateIcon className='mr-2'/>Deactivate
                </MenuItem>
              </Menu>
            </Grid>
            {/* <Grid item xs={3}>
              <Box sx={{display: 'flex', justifyContent:'space-between', alignItems:'center', border: '1px solid', borderColor: 'grey.300', borderRadius: 1 }}>
                <Box>
                  <SyncAlt sx={{transform: 'rotate(90deg)'}}/>
                  <span>Name[A-Z]</span>
                </Box>
                <IconButton
                  onClick={handleClick}
                  size="small"
                  sx={{ ml: 2 }}
                  aria-controls={open ? 'settings-menu' : undefined}
                  aria-haspopup="true"
                  aria-expanded={open ? 'true' : undefined}
                >
                  <KeyboardArrowDown/>
                </IconButton>
              </Box>
              <Menu
                anchorEl={anchorEl}
                id="settings-menu"
                open={open}
                onClose={handleClose}
                onClick={handleClose}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              >
                <MenuItem onClick={handleClose}>
                  <DeactivateIcon className='mr-2'/>Deactivate
                </MenuItem>
              </Menu>
            </Grid> */}
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ border: '1px solid', borderColor: 'grey.300', borderRadius: 1, overflow: 'auto', minHeight:'100px', maxHeight: '200px'}}>
              {
                storeList.map((store, index) => (
                  <Box sx={{ display: 'flex', flexDirection: 'column', ml: 2 }} key={store.label}>
                    <FormControlLabel
                      label={store.label}
                      control={<Checkbox checked={store.checked} onChange={() => handleCheckStore(index)} />}
                    />
                  </Box>
                ))
              }
            </Box>
          </Grid>
          <Grid item container direction='row-reverse' justifyContent='flex-start'>
            <Button className='button red small' sx={{marginLeft: '1em !important'}} onClick={saveChanges}>Save</Button>
            <Button className='button white small' onClick={onClose}>Cancel</Button>
          </Grid>
        </Grid>
      </Box>

      {
        showModal && (
          <WarningModal
            open={showModal}
            warningText={`Are you sure you want to deactivate ${settingLabel} for the selected stores?`}
            confirmation={deactivateSelected}
            onClose={closeModal}
          />
        )
      }
    </Box>
  )
}

SettingsStoreList.propTypes = {
  onClose: PropTypes.func,
  settingLabel: PropTypes.string,
  settingSelected: PropTypes.string,
  fullStoreList: PropTypes.array,
  openSuccessModal: PropTypes.func,
  closeSettings: PropTypes.func
}

export default SettingsStoreList;